// Shared constants / helpers for the Risk Detail workflow sections.

// Unwrap the SbomApiIntercepter / AIGApi response envelope.
export const extractItem = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

// Fallback 7-stage ISO blueprint when the org has no saved workflow config.
export const DEFAULT_BLUEPRINT = [
    { id: 'identification', name: 'Identify', owner_role: 'risk_manager', next_stages: ['assessment'] },
    { id: 'assessment', name: 'Assess', owner_role: 'risk_manager', next_stages: ['treatment'] },
    { id: 'treatment', name: 'Treatment Planning', owner_role: 'risk_owner', next_stages: ['review'] },
    { id: 'review', name: 'Review', owner_role: 'risk_reviewer', next_stages: ['acceptance', 'treatment'] },
    { id: 'acceptance', name: 'Acceptance', owner_role: 'business_owner', next_stages: ['monitoring', 'treatment'] },
    { id: 'monitoring', name: 'Monitoring', owner_role: 'risk_manager', next_stages: ['closed', 'assessment'] },
    { id: 'closed', name: 'Closed', owner_role: null, next_stages: [] },
];

// Section keys used by the stage→section gating in RiskDetail (index.js). Each
// maps to one workflow panel component. `comments` is intentionally omitted —
// it's cross-cutting and always shown regardless of stage.
export const SECTION_LABELS = {
    assessment: 'Risk Assessment',
    quantification: 'Financial Quantification (FAIR)',
    treatment: 'Treatment Plans',
    evidence: 'Evidence',
    bowtie: 'Bow-Tie Analysis',
    novaai: 'FORESEE AI',
    comments: 'Comments',
};

// Default per-stage section mapping. Overridable per-org via the `stage_sections`
// key in risk_portal settings (RiskWorkflowConfig role_config._settings). Keyed
// by stage id (see DEFAULT_BLUEPRINT), following the reference portal's stage_type
// semantics: one panel per stage, related pairs kept together. Comments lives on
// the final (Closed) stage, not always-on.
export const DEFAULT_STAGE_SECTIONS = {
    identification: ['novaai'],
    assessment: ['assessment'],
    treatment: ['treatment'],
    review: ['evidence'],
    acceptance: ['quantification'],
    monitoring: ['bowtie'],
    closed: ['comments'],
};

export const LIKELIHOOD_LABELS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
export const IMPACT_LABELS = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'];
export const liOptions = (labels) => labels.map((l, i) => ({ value: i + 1, label: `${i + 1} - ${l}` }));

export const CONTROL_EFFECTIVENESS_OPTIONS = [
    { value: 0, label: 'None (0%)' }, { value: 25, label: 'Weak (25%)' }, { value: 50, label: 'Moderate (50%)' },
    { value: 75, label: 'Strong (75%)' }, { value: 100, label: 'Effective (100%)' },
];
export const VELOCITY_OPTIONS = ['immediate', 'days', 'weeks', 'months', 'years']
    .map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
export const STRATEGY_OPTIONS = [
    { value: 'mitigate', label: 'Mitigate — Reduce likelihood/impact' },
    { value: 'transfer', label: 'Transfer — Insurance/outsourcing' },
    { value: 'accept', label: 'Accept — Within appetite' },
    { value: 'avoid', label: 'Avoid — Eliminate the activity' },
];
export const ACTION_PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical']
    .map((p) => ({ value: p, label: p[0].toUpperCase() + p.slice(1) }));

export const ratingOf = (s) => (s >= 16 ? 'Critical' : s >= 10 ? 'High' : s >= 5 ? 'Medium' : 'Low');
const RATING_TOKEN = { critical: 'var(--color-critical)', high: 'var(--color-high)', medium: 'var(--color-medium)', low: 'var(--color-low)' };
export const ratingColor = (r) => RATING_TOKEN[String(r || '').toLowerCase()] || 'var(--color-text-muted)';
export const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
export const money = (n) => `$${Number(n || 0).toLocaleString()}`;

// Display name for an evidence record: the name the user entered while uploading,
// with the original file's extension appended so the format shows (entered "ssss"
// + ".pdf" → "ssss.pdf"). Falls back to the raw file name when no name was entered.
export const evidenceDisplayName = (ev) => {
    const entered = (ev?.name || ev?.description || '').trim();
    if (!entered) return ev?.file_name || '';
    const dot = ev?.file_name ? ev.file_name.lastIndexOf('.') : -1;
    const ext = dot > 0 ? ev.file_name.slice(dot) : '';
    return ext && !entered.toLowerCase().endsWith(ext.toLowerCase()) ? `${entered}${ext}` : entered;
};